import {
	HubTemplateListItemModel
} from "client/Api/Hub/Templates/hubTemplates";
import { ReactElement, useMemo } from "react";
import { HubTemplateCard } from "./HubTemplateCard";
import { generateTrackingId, setTrackingId } from "client/Misc/utils";
import { Action, ActionKind } from "client_legacy/ndaw/actions";
import { localize } from "client_legacy/ndaw/culture";
import styles from "./TemplateList.module.less";
import { HubItemKind } from "client/Api/Hub/hubItemCategories";
import { createLocalDataSource } from "client/Hooks";
import { DataSourceSortDirection, DataSourceSortableState } from "client/Data/sorting";
import { HubItemMediaPreviewViewModel } from "client/Api/Hub/common";

export type TemplateListProps = {
	itemKind: HubItemKind;
	items: readonly HubTemplateListItemModel[];
	canManageTemplates: boolean;
	selectedItemId: number | nil;
	onSelectedItemIdChange(id: number | null): void;
	onChoose(templateId?: number): void;
	onEdit(id: number): void;
	onDuplicate(id: number): void;
	onRename(id: number, currentName: string): void;
	onDelete(id: number): void;
};

export const TemplateList = (props: TemplateListProps): ReactElement => {
	const {
		itemKind,
		items,
		canManageTemplates,
		selectedItemId,
		onSelectedItemIdChange,
		onChoose,
		onEdit,
		onDuplicate,
		onRename,
		onDelete
	} = props;

	const {
		currentItems
	} = useDataSource(
		items,
		{ sorting: sortByTemplateName }
	);

	return (
		<ul
			{...setTrackingId("template-list", `|${itemKind}`)}
			className={styles.root}
		>
			<BlankItem
				itemKind={itemKind}
				onClick={() => onSelectedItemIdChange(null)}
				onDoubleClick={() => onChoose()}
				isSelected={selectedItemId === null}
			/>
			{currentItems.map(
				x => (
					<Item
						key={x.id}
						id={x.id}
						kind={itemKind}
						name={x.name}
						canManageTemplates={canManageTemplates}
						onClick={() => onSelectedItemIdChange(x.id)}
						onDoubleClick={() => onChoose(x.id)}
						onDelete={onDelete}
						onDuplicate={onDuplicate}
						onEdit={onEdit}
						onRename={onRename}
						isSelected={selectedItemId === x.id}
						media={x.media}
					/>
				)
			)}
		</ul>
	);
};
TemplateList.displayName = "TemplateList";

type ItemProps = {
	id: number;
	kind: HubItemKind;
	name: string;
	canManageTemplates: boolean;
	isSelected: boolean;
	media: HubItemMediaPreviewViewModel;
	onClick(): void;
	onDoubleClick(): void;
	onEdit(id: number): void;
	onDuplicate(id: number): void;
	onRename(id: number, currentName: string): void;
	onDelete(id: number): void;
};

const Item = (props: ItemProps): ReactElement => {
	const {
		id,
		kind,
		name,
		canManageTemplates,
		isSelected,
		media,
		onClick,
		onDoubleClick,
		onDelete,
		onDuplicate,
		onEdit,
		onRename
	} = props;

	const actions = useMemo(
		(): readonly Action<ItemActionContext>[] => getActions({
			onEdit,
			onDelete,
			onDuplicate,
			onRename
		}),
		[
			onDelete,
			onDuplicate,
			onEdit,
			onRename
		]
	);

	const actionContext = useMemo(
		(): ItemActionContext => ({ id, name, canManageTemplates }),
		[id, name, canManageTemplates]
	);

	return (
		<li>
			<HubTemplateCard
				actions={actions}
				actionContext={actionContext}
				actionsTitle={name}
				kind={kind}
				name={name}
				isSelected={isSelected}
				onClick={onClick}
				onDoubleClick={onDoubleClick}
				trackingId={generateTrackingId("item",`|${id}`)}
				tooltip={name}
				media={media}
			/>
		</li>
	);
};
Item.displayName = "Item";

type ItemActionHandlers = {
	onEdit(id: number): void;
	onDuplicate(id: number): void;
	onRename(id: number, currentName: string): void;
	onDelete(id: number): void;
};

type ItemActionContext = {
	id: number;
	name: string;
	canManageTemplates: boolean;
};

function getActions(
	handlers: ItemActionHandlers
): readonly Action<ItemActionContext>[] {
	const {
		onEdit,
		onDuplicate,
		onRename,
		onDelete
	} = handlers;

	return [
		{
			kind: ActionKind.Regular,
			name: "editTemplate",
			title: localize("_EditTemplate"),
			canExecute: c => c.canManageTemplates,
			execute: (c) => onEdit(c.id),
			icon: "pen"
		},
		{
			kind: ActionKind.Regular,
			name: "duplicateTemplate",
			title: localize("_DuplicateTemplate"),
			canExecute: c => c.canManageTemplates,
			execute: (c) => onDuplicate(c.id),
			icon: "copy"
		},
		{
			kind: ActionKind.Regular,
			name: "renameTemplate",
			title: localize("_RenameTemplate"),
			canExecute: c => c.canManageTemplates,
			execute: (c) => onRename(c.id, c.name),
			icon: "text"
		},
		{
			kind: ActionKind.Regular,
			name: "deleteTemplate",
			title: localize("_DeleteTemplate"),
			canExecute: c => c.canManageTemplates,
			execute: (c) => onDelete(c.id),
			icon: "trash-can"
		},
	];
}

type BlankItemProps = {
	itemKind: HubItemKind;
	isSelected: boolean;
	onClick(): void;
	onDoubleClick(): void;
};

const BlankItem = (props: BlankItemProps): ReactElement => {
	const {
		itemKind,
		isSelected,
		onClick,
		onDoubleClick
	} = props;

	return (
		<li>
			<HubTemplateCard
				name={localize(itemKindToLocalizationKeysMap[itemKind])}
				onClick={onClick}
				onDoubleClick={onDoubleClick}
				isSelected={isSelected}
				trackingId="blank-template-item"
			/>
		</li>
	);
};
BlankItem.displayName = "BlankItem";

const itemKindToLocalizationKeysMap: { [key in HubItemKind]: LocalizationKey } = {
	[HubItemKind.Announcement]: "_BlankAnnouncement",
	[HubItemKind.KnowledgeItem]: "_BlankKnowledgeArticle",
	[HubItemKind.Event]: "_BlankEvent"
};

const useDataSource = createLocalDataSource<HubTemplateListItemModel>()
	.setConfiguration({})
	.getHook();

const sortByTemplateName: DataSourceSortableState<HubTemplateListItemModel> = {
	direction: DataSourceSortDirection.Ascending,
	propertyName: "name"
};